import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import ReactModal from "react-modal";
import Select, { SingleValue } from "react-select";
import {
  QualityAlert,
  QualityAlertLocationInfo,
} from "../../../models/quality-alert";
import { Supplier } from "../../../models/supplier";
import { Location } from "../../../models/location";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import PartSelector from "./partSelector";
import { Modal } from "../modal";
import { QualityAlertDiscrepantMaterial } from "../../../models/quality-alert";
import Attachments from "./qualityAlertModalAttachments";
import SupplierDirectiveBox from "./supplierDirectiveBox";
import ProblemDetails from "./problemDetails";
import { useAppDispatch, useAppSelector } from "../../../reducers/store";
import { deleteAttachment } from "../../../reducers/attachmentSlice";
import { saveQualityAlert as saveQualityAlertAction } from "../../../reducers/qualityAlertSlice";
import { 
  getSupplierData,
  selectAllSuppliers,
  selectParentSuppliers,
  selectIsLoading,
  selectError 
} from "../../../reducers/supplierSlice";

ReactModal.setAppElement("#root");

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "50vw",
    height: "50vh",
    overflow: "auto",
  },
};

interface ModalHandler {
  openModal: (header?: string, width?: string, height?: string) => void;
  closeModal: () => void;
}

// set up props for the modal ADAMTODO does this need to be in the forward ref too
interface QualityAlertModalProps {
  location: Location | null;
  children: JSX.Element;
  allLocations: Location[];
}

export const AddQualityAlertModal = forwardRef<
  ModalHandler,
  QualityAlertModalProps
>(({ location, children, allLocations }, ref) => {
  const dispatch = useAppDispatch();
  const allSuppliersFromStore = useAppSelector(selectAllSuppliers);
  const parentSuppliersFromStore = useAppSelector(selectParentSuppliers);
  const isLoading = useAppSelector(selectIsLoading);
  const error = useAppSelector(selectError);

  const [qualityAlert, setQualityAlert] = useState<QualityAlert | null>(
    {} as QualityAlert,
  );
  const [startDate, setStartDate] = useState(new Date());
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier>();
  const [contacts, setContacts] = useState<any[]>([]);
  const [modalIsOpen, setIsOpen] = useState<boolean>(false);
  const [modalWidth, setModalWidth] = useState<string>("50vw");
  const [modalHeight, setModalHeight] = useState<string>("50vh");
  const [modalHeader, setModalHeader] = useState<string>("");
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(
    null,
  );
  const [discrepantMaterialsModalOpen, setDiscrepantmaterialsModalOpen] =
    useState<boolean>(false);
  const [qualityAlertLocationInfo, setQualityAlertLocationInfo] =
    useState<QualityAlertLocationInfo>({} as QualityAlertLocationInfo);

  const [qualityAlertLocationInfoArray, setQualityAlertLocationInfoArray] =
    useState<QualityAlertLocationInfo[]>([]);
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(false);

  useEffect(() => {
    if (location) {
      setSelectedLocation(location);
      setLocationDetails(location);
    }
  }, [location]);

  useEffect(() => {
    if (modalIsOpen) {
      dispatch(getSupplierData());
    }
  }, [modalIsOpen]);

  useEffect(() => {
    if (allSuppliersFromStore.length > 0) {
      setParentData(allSuppliersFromStore);
    }
  }, [allSuppliersFromStore]);

  useEffect(() => {
    if (selectedSupplier) {
      setContacts(selectedSupplier.contacts);
    }
  }, [selectedSupplier]);
  // only for debugging
  // useEffect(() => {
  //   console.log("quality alert update ", qualityAlert);
  // }, [qualityAlert]);

  useImperativeHandle(ref, () => ({
    openModal(
      header: string = "",
      width: string = "50vw",
      height: string = "50vh",
    ) {
      setModalWidth(width);
      setModalHeight(height);
      setModalHeader(header);
      setIsOpen(true);
    },
    closeModal() {
      setIsOpen(false);
    },
  }));

  const setParentData = (data: Supplier[]) => {
    // un commenting this code breaks the portal if the user running it has nothing in their local storage
    // const localStorageSupplier: Supplier = localStorage.getItem(
    //   "selected_supplier",
    // )
    //   ? JSON.parse(localStorage.getItem("selected_supplier") ?? "")
    //   : parentSuppliersFromStore[0];

    //selectSupplier(localStorageSupplier, data);
  };

  const closeModal = () => {
    if (qualityAlert && qualityAlert?.attachments.length > 0) {
      qualityAlert.attachments.forEach((attachment) => {
        dispatch(deleteAttachment(attachment.attachment));
      });
    }
    setIsOpen(false);
    resetToDefault();
  };

  function resetToDefault(): void {
    setQualityAlert({} as QualityAlert);
    setStartDate(new Date());
    setSelectedSupplier(undefined);
    setContacts([]);
    setIsOpen(false);
    setModalWidth("50vw");
    setModalHeight("50vh");
    setModalHeader("");
    setSelectedLocation(location);
    setDiscrepantmaterialsModalOpen(false);
    setQualityAlertLocationInfo({} as QualityAlertLocationInfo);
    setQualityAlertLocationInfoArray([]);
  }

  function onContactDropdownChange(d: any) {
    if (!qualityAlert) return;
    qualityAlert.supplierContact = d.id;
    setQualityAlert({ ...qualityAlert });
  }

  function onLocationDropdownChange(e: any) {
    if (!e || !qualityAlert) return;
    setSelectedLocation(e);
    setLocationDetails(e);
  }

  function setLocationDetails(location: Location): void {
    if (!qualityAlert || !location.agreement) return;
    qualityAlert.location = location.id;
    qualityAlertLocationInfo.location = location.id;
    qualityAlertLocationInfo.agreement = location.agreement?.id;
    setQualityAlert({ ...qualityAlert });
    setQualityAlertLocationInfo({ ...qualityAlertLocationInfo });
  }

  function onSupplierDropdownChange(
    e: SingleValue<{ id: number; name: string }>,
  ): void {
    if (!e || !qualityAlert || typeof e.id !== 'number') return;
    
    qualityAlert.supplier = e.id;
    qualityAlertLocationInfo.supplier = e.id;
    setQualityAlert({ ...qualityAlert });
    setQualityAlertLocationInfo({ ...qualityAlertLocationInfo });
    // set contacts to the selected supplier contacts array
    setSelectedSupplier(allSuppliersFromStore.find((s) => s.id === e.id));
  }

  function handleDateChange(date: Date | null): void {
    if (!date || !qualityAlert) return;
    qualityAlert.time = date;
    setStartDate(date);
    setQualityAlert(qualityAlert);
  }

  function addDiscrepantMaterial(
    discrepantMaterial: QualityAlertDiscrepantMaterial,
  ): void {
    if (!qualityAlert || !qualityAlert.discrepantMaterialCount) return;
    qualityAlert.discrepantMaterialCount++;
    setQualityAlert(qualityAlert);
  }

  function supplierDirectiveBoxChecked(e: any): void {
    // TODO adam I dont think this is needed

  }

  function saveQualityAlert(): void {
    if (!qualityAlert || isSaveDisabled) return;
    setIsSaveDisabled(true);
    qualityAlert.qualityAlertLocationInfo = [qualityAlertLocationInfo];
    setQualityAlert(qualityAlert);
    dispatch(saveQualityAlertAction(qualityAlert));
    setIsOpen(false);
    setIsSaveDisabled(false);
  }

  return (
    <ReactModal
      isOpen={modalIsOpen}
      onRequestClose={closeModal}
      style={{
        content: {
          ...customStyles.content,
          width: modalWidth,
          height: modalHeight,
        },
      }}
    >
      <div className="w-[50 %] flex h-[100%] flex-col justify-normal">
        <div className="flex h-[50px] w-[100%] items-center justify-between">
          <span className="text-[25px] font-bold text-[black]">
            Create Quality Issue
          </span>
          <div className="flex cursor-pointer items-center justify-center text-[26px] text-[black]">
            <FontAwesomeIcon icon={faXmark} onClick={closeModal} />
          </div>
        </div>
        <span className="text-[14px] text-[grey]">
          Use the form below to submit a Quality Alert or Near Miss to all of
          your field reps. Once submitted, they will be notified of the issue
          you have submitted
        </span>
        <div className="justify-normal pl-8 text-left">
          <span className="text-[15px] text-black">Location</span>
          <Select
            className="h-[2.5em] w-[50%] text-[1em] text-black"
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id?.toString()}
            options={allLocations ? allLocations : []}
            value={selectedLocation}
            styles={{
              control: (base) => ({
                ...base,
                height: 30,
                minHeight: 30,
                maxHeight: 30,
              }),
              indicatorsContainer: (base) => ({
                ...base,
                height: 30,
                minHeight: 30,
                maxHeight: 30,
              }),
              valueContainer: (base) => ({
                ...base,
                height: 30,
                minHeight: 30,
                maxHeight: 30,
              }),
              menu: (base) => ({
                ...base,
                paddingTop: 0,
                paddingBottom: 0,
              }),
            }}
            onChange={(d) => onLocationDropdownChange(d)}
          />
          <span className="text-[15px] text-black">Supplier</span>
          <Select
            id="select2"
            className="h-[2.5em] w-[50%] text-[1em] text-black"
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id?.toString()}
            options={allSuppliersFromStore}
            form="Select2"
            styles={{
              control: (base) => ({
                ...base,
                height: 30,
                minHeight: 30,
                maxHeight: 30,
              }),
              indicatorsContainer: (base) => ({
                ...base,
                height: 30,
                minHeight: 30,
                maxHeight: 30,
              }),
              valueContainer: (base) => ({
                ...base,
                height: 30,
                minHeight: 30,
                maxHeight: 30,
              }),
              menuList: (base) => ({
                ...base,
                paddingTop: 0,
                paddingBottom: 0,
              }),
            }}
            onChange={(e) => onSupplierDropdownChange(e)}
          />
          <span className="text-[15px] text-black">Supplier Contact</span>
          <Select
            className="h-[2.5em] w-[50%] text-[1em] text-black"
            getOptionLabel={(option) =>
              option.firstName + " " + option.lastName
            }
            getOptionValue={(option) => option.id?.toString()}
            options={contacts}
            styles={{
              control: (base) => ({
                ...base,
                height: 30,
                minHeight: 30,
                maxHeight: 30,
              }),
              indicatorsContainer: (base) => ({
                ...base,
                height: 30,
                minHeight: 30,
                maxHeight: 30,
              }),
              valueContainer: (base) => ({
                ...base,
                height: 30,
                minHeight: 30,
                maxHeight: 30,
              }),
            }}
            onChange={(d) => onContactDropdownChange(d)}
          />
          <div className="border-gray-200">
            <DatePicker
              className="h-[2.5em] w-[50%] border-[2px] border-gray-200 bg-white text-[1em] text-black"
              selected={startDate}
              disabled={true}
              onChange={handleDateChange}
            />
          </div>
        </div>

        {/* Parts table needs to go here with part.desc, part.number, part.productionTeam */}

        <PartSelector
          partsList={selectedSupplier?.parts ?? []}
          onDiscrepanctMaterialChange={addDiscrepantMaterial}
          qualityAlert={qualityAlert}
          updateQualityAlert={setQualityAlert}
          supplierId={typeof qualityAlert?.supplier === 'number' ? qualityAlert.supplier : undefined}
          qualityAlertLocationInfo={qualityAlertLocationInfo}
          setQualityAlertLocationInfo={setQualityAlertLocationInfo}
        />
        {discrepantMaterialsModalOpen && (
          <div className="absolute left-[50px] top-[42px] z-[999999999] w-[250px] border-[1px] border-[#E5E5E5] bg-[white] p-[12px]">
            <Modal>
              <span className="text-[15px] text-black">
                Discrepant Materals
              </span>
            </Modal>
          </div>
        )}

        <ProblemDetails
          qualityAlert={qualityAlert}
          updateQualityAlert={setQualityAlert}
          location={selectedLocation}
          qualityAlertLocationInfo={qualityAlertLocationInfo}
          setQualityAlertLocationInfo={setQualityAlertLocationInfo}
        />

        <div className="pl-8 text-left">
          <SupplierDirectiveBox
            qualityAlert={qualityAlert}
            onCheck={supplierDirectiveBoxChecked}
            updateQualityAlert={setQualityAlert}
          />
        </div>
        <span className="pl-8 text-[15px] text-black">Attachments</span>
        <div className="pl-8 text-left">
          <Attachments
            qualityAlert={qualityAlert}
            updateQualityAlert={setQualityAlert}
          />
        </div>
        <div className="flex justify-end">
          <div className="flex justify-end p-1.5">
            <div
              className="mt-2 cursor-pointer rounded bg-red-600 p-1.5 text-center text-sm text-white hover:bg-gray-400"
              onClick={closeModal}
            >
              {"Cancel"}
            </div>
          </div>
          <div className="flex justify-end p-1.5">
            <div
              className="mt-2 cursor-pointer rounded bg-green-600 p-1.5 text-center text-sm text-white hover:bg-gray-400"
              onClick={saveQualityAlert}
            >
              {"Save"}
            </div>
          </div>
        </div>
      </div>
    </ReactModal>
  );
});
